import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Baby, Heart, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { toast } from 'sonner';

/**
 * V13.14.7 — Mandatory postnatal onboarding modal.
 *
 * Triggered when the backend has flagged `user.pending_postnatal_onboarding === true`
 * (set by the MTTF→Postnatal admin migration). Cannot be dismissed until the
 * learner submits delivery date + delivery mode.
 *
 * Required for clinical safety: knowing whether the learner had a natural or
 * C-section delivery determines which postnatal exercises are safe.
 */
export default function PostnatalOnboardingModal({ open }) {
  const { user, refreshUser } = useAuth();
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDeliveryDate('');
    setDeliveryMode('');
  }, [open]);

  // Bounds: today (max) and 24 months ago (min)
  const today = new Date();
  const maxDate = today.toISOString().slice(0, 10);
  const minDate = new Date(today.getTime() - 730 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const canSubmit = deliveryDate && deliveryMode && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post('/api/me/postnatal-onboarding', {
        delivery_date: deliveryDate,
        delivery_mode: deliveryMode,
      });
      toast.success('Saved! Welcome to your postnatal journey 💛');
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save — please try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* non-dismissable until submitted */ }}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideClose
        data-testid="postnatal-onboarding-modal"
      >
        <DialogHeader>
          <div className="flex items-center justify-center mb-2">
            <div className="rounded-full bg-rose-100 p-3"><Baby className="h-7 w-7 text-rose-600" /></div>
          </div>
          <DialogTitle className="font-heading text-xl text-center">Welcome to your postnatal journey, {user?.name?.split(' ')[0] || 'mama'} 💛</DialogTitle>
          <DialogDescription className="text-center text-sm pt-1">
            We've moved you to our <strong>postnatal program</strong>. To make sure your sessions
            stay safe and effective, please share two quick details.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-1.5">
              <Heart className="h-4 w-4 text-rose-500" />When did you deliver?
            </label>
            <Input
              type="date"
              value={deliveryDate}
              max={maxDate}
              min={minDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              data-testid="postnatal-delivery-date"
            />
            <p className="text-[11px] text-muted-foreground">
              This helps your instructor pace your recovery exercises correctly.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">How did you deliver?</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDeliveryMode('vaginal')}
                className={`rounded-xl border-2 p-3 text-left transition-all ${
                  deliveryMode === 'vaginal'
                    ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                    : 'border-border hover:border-emerald-200'
                }`}
                data-testid="postnatal-mode-vaginal"
              >
                <div className="text-sm font-semibold">Vaginal birth</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Including water birth & assisted</div>
              </button>
              <button
                type="button"
                onClick={() => setDeliveryMode('c_section')}
                className={`rounded-xl border-2 p-3 text-left transition-all ${
                  deliveryMode === 'c_section'
                    ? 'border-rose-500 bg-rose-50 ring-2 ring-rose-200'
                    : 'border-border hover:border-rose-200'
                }`}
                data-testid="postnatal-mode-csection"
              >
                <div className="text-sm font-semibold">C-section</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Planned or emergency</div>
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Some core / abdominal exercises differ for vaginal vs C-section recovery — this is purely for your safety.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="w-full"
            data-testid="postnatal-submit"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : 'Confirm and continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
